import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {ExamModule} from '@cp/model';
import TopicSelection from '../../../components/TopicSelection/TopicSelection';
import useAddSelectedModules from '../../../hooks/users/useAddSelectedModules';

export interface AddModuleDialogProps {
  open: boolean;
  onClose: () => void;
  availableModules: ExamModule[];
  userSelectedModuleIds: string[];
}

export default function AddModuleDialog({
  open,
  onClose,
  availableModules,
  userSelectedModuleIds,
}: AddModuleDialogProps) {
  const [selectedModule, setSelectedModule] = useState<ExamModule | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const {addSelectedModules, loading} = useAddSelectedModules();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const topicSelectionRef = useRef<HTMLDivElement>(null);

  // Filter out modules that user already has selected
  const availableModulesToAdd = availableModules.filter((module) => !userSelectedModuleIds.includes(module.id));

  // Auto-scroll to topic selection on mobile when module is selected
  useEffect(() => {
    if (selectedModule && isMobile && topicSelectionRef.current) {
      const timer = setTimeout(() => {
        topicSelectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [selectedModule, isMobile]);

  const handleModuleSelect = (module: ExamModule) => {
    setSelectedModule(module);
    setSelectedTopics([]);
  };

  const handleTopicSelectionChange = (topics: string[]) => {
    setSelectedTopics(topics);
  };

  const handleAddModule = async () => {
    if (!selectedModule || selectedTopics.length === 0) return;

    try {
      await addSelectedModules(selectedModule.id, selectedTopics);
      onClose();
      // Reset state
      setSelectedModule(null);
      setSelectedTopics([]);
    } catch (error) {
      console.error('Error adding module:', error);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedModule(null);
    setSelectedTopics([]);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-testid="add-module-dialog">
      <DialogTitle>Add Study Module</DialogTitle>
      <DialogContent>
        <Box sx={{display: 'flex', gap: 2, minHeight: 400}}>
          {/* Module Selection */}
          <Box sx={{flex: 1, minWidth: 300}}>
            <Typography variant="h6" gutterBottom>
              Select Module
            </Typography>
            {availableModulesToAdd.length === 0 ? (
              <Typography color="text.secondary">All available modules have been added to your account.</Typography>
            ) : (
              <List sx={{maxHeight: 300, overflow: 'auto'}}>
                {availableModulesToAdd.map((module) => (
                  <ListItem key={module.id} disablePadding>
                    <ListItemButton
                      selected={selectedModule?.id === module.id}
                      onClick={() => handleModuleSelect(module)}
                      data-testid="module-checkbox"
                      data-module-id={module.id}
                      data-module-name={module.name}
                    >
                      <ListItemText
                        primary={module.name}
                        secondary={`${module.topics.length} topics • ${module.examBoard || 'No exam board'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Topic Selection */}
          {selectedModule && (
            <Box
              ref={topicSelectionRef}
              sx={{
                flex: 1,
                minWidth: 300,
                // Fix for MUI TreeView accessibility issue with aria-hidden and focus
                '& .MuiTreeItem-checkbox': {
                  '&[aria-hidden="true"]': {
                    '& input:focus': {
                      '& ~ *': {
                        'aria-hidden': 'false !important',
                      },
                    },
                  },
                },
                // Additional fix for the specific error pattern
                '& .MuiTreeItem-checkbox[aria-hidden="true"] input:focus ~ *': {
                  'aria-hidden': 'false !important',
                },
              }}
            >
              <Typography variant="h6" sx={{mb: 2}}>
                Select Topics ({selectedTopics.length} selected)
              </Typography>
              <TopicSelection
                moduleTopics={selectedModule.topics}
                selectedTopics={selectedTopics}
                onSelectionChange={handleTopicSelectionChange}
                height={300}
                showSelectAll
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleAddModule}
          variant="contained"
          disabled={!selectedModule || selectedTopics.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          data-testid="save-module-button"
        >
          Add Module
        </Button>
      </DialogActions>
    </Dialog>
  );
}
